import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { chatService } from '../../services/chatService';
import Header from '../../components/navigation/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { cn } from '../../utils/cn';
import AgentCard from './components/AgentCard';
import MessageList from './components/MessageList';
import ConversationList from './components/ConversationList';
import TeamNotificationPanel from '../../components/navigation/TeamNotificationPanel';

const AgentChat = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [activeAgent, setActiveAgent] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const messageChannelRef = useRef(null);
  const messageUpdateChannelRef = useRef(null);
  const agentStatusChannelRef = useRef(null);
  const conversationChannelRef = useRef(null);
  const notificationChannelRef = useRef(null);
  const typingChannelRef = useRef(null);
  const presenceChannelRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Load initial data
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Load agents and projects with proper error handling
        const agentsPromise = chatService?.getAgents()?.catch(err => {
          console.error('Failed to load agents:', err);
          return [];
        });
        
        const projectsPromise = chatService?.getProjects()?.catch(err => {
          console.error('Failed to load projects:', err);
          return [];
        });
        
        const conversationsPromise = chatService?.getConversations(user?.id)?.catch(err => {
          console.error('Failed to load conversations:', err);
          return [];
        });

        const [agentsData, projectsData, conversationsData] = await Promise.all([
          agentsPromise,
          projectsPromise,
          conversationsPromise
        ]);

        setAgents(agentsData || []);
        setProjects(projectsData || []);
        setConversations(conversationsData || []);

        // Auto-select first conversation if available
        if (conversationsData && conversationsData?.length > 0) {
          setSelectedConversation(conversationsData?.[0]);
        }
      } catch (err) {
        console.error('Error loading chat data:', err);
        setError(err?.message || 'Failed to load chat data');
      } finally {
        // CRITICAL: Always set loading to false
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  // Load messages when conversation selected
  useEffect(() => {
    if (!selectedConversation) return;

    const loadMessages = async () => {
      try {
        const messagesData = await chatService?.getMessages(selectedConversation?.id);
        setMessages(messagesData);
      } catch (err) {
        setError(err?.message || 'Failed to load messages');
      }
    };

    loadMessages();

    // Subscribe to new messages
    messageChannelRef.current = chatService?.subscribeToMessages(
      selectedConversation?.id,
      (newMessage) => {
        setMessages(prev => {
          // Avoid duplicates
          if (prev?.some(m => m?.id === newMessage?.id)) return prev;
          return [...prev, newMessage];
        });
      }
    );

    // Subscribe to message updates (for streaming)
    messageUpdateChannelRef.current = chatService?.subscribeToMessageUpdates(
      selectedConversation?.id,
      (updatedMessage) => {
        setMessages(prev => prev?.map(msg => 
          msg?.id === updatedMessage?.id ? updatedMessage : msg
        ));
      }
    );

    // Subscribe to typing indicators
    typingChannelRef.current = chatService?.subscribeToTypingIndicators(
      selectedConversation?.id,
      (typingData) => {
        if (typingData?.event === 'started' && typingData?.userId !== user?.id) {
          setTypingUsers(prev => {
            if (prev?.some(u => u?.userId === typingData?.userId)) return prev;
            return [...prev, { userId: typingData?.userId, userName: typingData?.userName }];
          });
        } else if (typingData?.event === 'stopped') {
          setTypingUsers(prev => prev?.filter(u => u?.userId !== typingData?.userId));
        }
      }
    );

    // Update user presence
    if (user?.id) {
      chatService?.updateUserPresence(user?.id, 'online', selectedConversation?.id);
    }

    return () => {
      if (messageChannelRef?.current) {
        chatService?.unsubscribe(messageChannelRef?.current);
      }
      if (messageUpdateChannelRef?.current) {
        chatService?.unsubscribe(messageUpdateChannelRef?.current);
      }
      if (typingChannelRef?.current) {
        chatService?.unsubscribe(typingChannelRef?.current);
      }
      // Clear typing indicator on unmount
      if (user?.id && selectedConversation?.id) {
        chatService?.setTypingIndicator(selectedConversation?.id, user?.id, false);
      }
    };
  }, [selectedConversation, user]);

  // Subscribe to agent status changes
  useEffect(() => {
    agentStatusChannelRef.current = chatService?.subscribeToAgentStatus((updatedAgent) => {
      setAgents(prev => prev?.map(agent => 
        agent?.id === updatedAgent?.id 
          ? { ...agent, status: updatedAgent?.status, updatedAt: updatedAgent?.updatedAt }
          : agent
      ));

      // Update conversation list if agent status changed
      setConversations(prev => prev?.map(conv => 
        conv?.agentId === updatedAgent?.id
          ? { ...conv, agent: { ...conv?.agent, status: updatedAgent?.status } }
          : conv
      ));
    });

    return () => {
      if (agentStatusChannelRef?.current) {
        chatService?.unsubscribe(agentStatusChannelRef?.current);
      }
    };
  }, []);

  // Subscribe to conversation updates
  useEffect(() => {
    if (!user?.id) return;

    conversationChannelRef.current = chatService?.subscribeToConversations(
      user?.id,
      ({ event, conversation }) => {
        if (event === 'INSERT') {
          setConversations(prev => [conversation, ...prev]);
        } else if (event === 'UPDATE') {
          setConversations(prev => prev?.map(conv => 
            conv?.id === conversation?.id ? conversation : conv
          ));
        } else if (event === 'DELETE') {
          setConversations(prev => prev?.filter(conv => conv?.id !== conversation?.id));
        }
      }
    );

    return () => {
      if (conversationChannelRef?.current) {
        chatService?.unsubscribe(conversationChannelRef?.current);
      }
    };
  }, [user]);

  // Subscribe to notifications
  useEffect(() => {
    if (!user?.id) return;

    const loadNotifications = async () => {
      try {
        const notificationsData = await chatService?.getNotifications(user?.id, 20);
        setNotifications(notificationsData);
        setUnreadCount(notificationsData?.filter(n => !n?.isRead)?.length);
      } catch (err) {
        console.error('Failed to load notifications:', err);
      }
    };

    loadNotifications();

    notificationChannelRef.current = chatService?.subscribeToNotifications(
      user?.id,
      (newNotification) => {
        setNotifications(prev => [newNotification, ...prev]);
        setUnreadCount(prev => prev + 1);
        
        // Show browser notification if supported
        if ('Notification' in window && Notification?.permission === 'granted') {
          new Notification(newNotification?.title, {
            body: newNotification?.message,
            icon: '/favicon.ico'
          });
        }
      }
    );

    return () => {
      if (notificationChannelRef?.current) {
        chatService?.unsubscribe(notificationChannelRef?.current);
      }
    };
  }, [user]);

  // Subscribe to user presence
  useEffect(() => {
    presenceChannelRef.current = chatService?.subscribeToUserPresence((presenceData) => {
      // Update agent availability based on user presence if needed
      console.log('User presence updated:', presenceData);
    });

    // Set initial presence
    if (user?.id) {
      chatService?.updateUserPresence(user?.id, 'online');
    }

    // Update presence on window focus/blur
    const handleFocus = () => {
      if (user?.id) {
        chatService?.updateUserPresence(user?.id, 'online');
      }
    };

    const handleBlur = () => {
      if (user?.id) {
        chatService?.updateUserPresence(user?.id, 'away');
      }
    };

    window?.addEventListener('focus', handleFocus);
    window?.addEventListener('blur', handleBlur);

    return () => {
      if (presenceChannelRef?.current) {
        chatService?.unsubscribe(presenceChannelRef?.current);
      }
      window?.removeEventListener('focus', handleFocus);
      window?.removeEventListener('blur', handleBlur);
      
      // Set offline on unmount
      if (user?.id) {
        chatService?.updateUserPresence(user?.id, 'offline');
      }
    };
  }, [user]);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification?.permission === 'default') {
      Notification?.requestPermission();
    }
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Start new conversation
  const handleStartConversation = async (agent) => {
    if (!user) return;

    try {
      const newConversation = await chatService?.createConversation(
        user?.id,
        agent?.id,
        selectedProject || null,
        `Chat with ${agent?.name}`
      );

      setConversations(prev => [newConversation, ...prev]);
      setSelectedConversation(newConversation);
      setSelectedAgent(null);
    } catch (err) {
      setError(err?.message || 'Failed to start conversation');
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!messageInput?.trim() || !selectedConversation || !user) return;

    try {
      setSending(true);
      setError('');
      setActiveAgent(null);
      
      // Clear typing indicator
      await chatService?.setTypingIndicator(selectedConversation?.id, user?.id, false);
      
      // Send user message
      await chatService?.sendMessage(selectedConversation?.id, user?.id, messageInput?.trim());
      const currentMessage = messageInput?.trim();
      setMessageInput('');

      // Generate AI-powered agent response with routing (NO document dependencies)
      try {
        const agent = agents?.find(a => a?.id === selectedConversation?.agentId);
        if (agent) {
          // Get recent conversation history for context (last 10 messages)
          const recentHistory = messages?.slice(-10);
          
          const response = await chatService?.generateAndSendAgentResponse(
            selectedConversation?.id,
            agent,
            recentHistory,
            currentMessage
          );

          // Update active agent badge
          if (response?.agent) {
            setActiveAgent(response?.agent);
          }
        }
      } catch (err) {
        console.error('Agent response error:', err);
        setError(err?.message || 'Failed to generate agent response');
      }
    } catch (err) {
      console.error('Send message error:', err);
      setError(err?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle typing indicator
  const handleTyping = () => {
    if (!selectedConversation || !user?.id) return;

    // Set typing indicator
    chatService?.setTypingIndicator(selectedConversation?.id, user?.id, true);

    // Clear existing timeout
    if (typingTimeoutRef?.current) {
      clearTimeout(typingTimeoutRef?.current);
    }

    // Set new timeout to clear typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      chatService?.setTypingIndicator(selectedConversation?.id, user?.id, false);
    }, 3000);
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      await chatService?.markNotificationRead(notification?.id);
      setNotifications(prev => prev?.map(n => 
        n?.id === notification?.id ? { ...n, isRead: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));

      // Navigate to related conversation if exists
      if (notification?.relatedConversationId) {
        const conversation = conversations?.find(c => c?.id === notification?.relatedConversationId);
        if (conversation) {
          setSelectedConversation(conversation);
          setShowNotifications(false);
        }
      }
    } catch (err) {
      console.error('Failed to handle notification:', err);
    }
  };

  // Mark all notifications as read
  const handleMarkAllRead = async () => {
    try {
      await chatService?.markAllNotificationsRead(user?.id);
      setNotifications(prev => prev?.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Filter conversations
  const filteredConversations = conversations?.filter(conv => {
    const matchesAgent = !filterAgent || conv?.agentId === filterAgent;
    const matchesProject = !filterProject || conv?.projectId === filterProject;
    return matchesAgent && matchesProject;
  });

  // Filter agents
  const filteredAgents = agents?.filter(agent => {
    if (filterAgent && agent?.id !== filterAgent) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading chat...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16 h-screen flex flex-col">
        {/* Page Header */}
        <div className="border-b border-border bg-card px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Icon name="MessageSquare" size={28} />
                Agent Chat
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Real-time chat with specialized carbon project agents
              </p>
            </div>
            <div className="flex items-center gap-3">
              {activeAgent && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                  {activeAgent === 'coordinator' && 'Coordinator'}
                  {activeAgent === 'pdd_design' && 'PDD Design Specialist'}
                  {activeAgent === 'mrv_gee' && 'MRV/GEE Expert'}
                  {activeAgent === 'regulatory_art6' && 'Article 6 Specialist'}
                  {activeAgent === 'financial_tokenization' && 'Financial Expert'}
                  {activeAgent === 'icvcm_vcmi' && 'ICVCM/VCMI Specialist'}
                </div>
              )}
              <div className="relative">
                <Button
                  variant="outline"
                  iconName="Bell"
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative"
                >
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
                {showNotifications && (
                  <TeamNotificationPanel
                    notifications={notifications}
                    unreadCount={unreadCount}
                    onNotificationClick={handleNotificationClick}
                    onMarkAllRead={handleMarkAllRead}
                    onClose={() => setShowNotifications(false)}
                  />
                )}
              </div>
              <Button
                variant="outline"
                iconName="Plus"
                onClick={() => setSelectedAgent('new')}
              >
                New Chat
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mt-4">
            <Select
              placeholder="Filter by agent"
              value={filterAgent}
              onChange={setFilterAgent}
              options={[
                { value: '', label: 'All Agents' },
                ...agents?.map(agent => ({
                  value: agent?.id,
                  label: agent?.name
                }))
              ]}
              clearable
              className="w-64"
            />
            <Select
              placeholder="Filter by project"
              value={filterProject}
              onChange={setFilterProject}
              options={[
                { value: '', label: 'All Projects' },
                ...projects?.map(project => ({
                  value: project?.id,
                  label: project?.name
                }))
              ]}
              clearable
              className="w-64"
            />
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Conversations Sidebar */}
          <div className="w-80 border-r border-border bg-card overflow-y-auto">
            <ConversationList
              conversations={filteredConversations}
              selectedConversation={selectedConversation}
              onSelectConversation={setSelectedConversation}
            />
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {selectedAgent === 'new' ? (
              <div className="flex-1 overflow-y-auto p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Select an Agent</h2>
                <div className="mb-4">
                  <Select
                    label="Project (Optional)"
                    placeholder="Select a project"
                    value={selectedProject}
                    onChange={setSelectedProject}
                    options={[
                      { value: '', label: 'No Project' },
                      ...projects?.map(project => ({
                        value: project?.id,
                        label: project?.name
                      }))
                    ]}
                    clearable
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredAgents?.map(agent => (
                    <AgentCard
                      key={agent?.id}
                      agent={agent}
                      onSelect={handleStartConversation}
                    />
                  ))}
                </div>
              </div>
            ) : selectedConversation ? (
              <>
                {/* Conversation Header */}
                <div className="border-b border-border bg-card px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="Bot" size={20} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">
                        {selectedConversation?.agent?.name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className={cn(
                          'w-2 h-2 rounded-full',
                          selectedConversation?.agent?.status === 'available' && 'bg-success',
                          selectedConversation?.agent?.status === 'busy' && 'bg-warning',
                          selectedConversation?.agent?.status === 'offline' && 'bg-muted'
                        )}></span>
                        <span className="capitalize">{selectedConversation?.agent?.status}</span>
                        {selectedConversation?.project && (
                          <>
                            <span>•</span>
                            <span>{selectedConversation?.project?.name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <MessageList
                  messages={messages}
                  currentUserId={user?.id}
                  agent={selectedConversation?.agent}
                  typingUsers={typingUsers}
                />
                <div ref={messagesEndRef} />

                {/* Message Input */}
                <div className="border-t border-border bg-card px-6 py-4">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      value={messageInput}
                      onChange={(e) => {
                        setMessageInput(e?.target?.value);
                        handleTyping();
                      }}
                      placeholder="Type your message..."
                      disabled={sending}
                      className="flex-1"
                    />
                    <Button
                      type="submit"
                      iconName="Send"
                      disabled={!messageInput?.trim() || sending}
                      loading={sending}
                    >
                      Send
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Icon name="MessageSquare" size={64} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Select a conversation or start a new chat</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="fixed bottom-4 right-4 bg-destructive text-destructive-foreground px-4 py-3 rounded-md shadow-lg flex items-center gap-2">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-2">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentChat;